import { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { useToast } from "../lib/toast";
import type { EventLineupView } from "../api/types";

interface SlotMap {
  [weightClass: number]: { rosterEntryId: string; wrestlerName: string } | undefined;
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c] as string,
  );
}

/**
 * Per-meet dual lineup board. Weight-class slots (from the host team's roster
 * config) on the left; the team roster as an assignable pool on the right.
 * Core interaction is tap-to-assign (works on mobile + keyboard); desktop also
 * supports native drag-and-drop. Admins edit + save; members see it read-only.
 */
export function EventLineupBoard({
  meetId,
  isAdmin,
  meetTitle,
  meetDate,
}: {
  meetId: string;
  isAdmin: boolean;
  meetTitle?: string;
  meetDate?: string;
}) {
  const toast = useToast();
  const [data, setData] = useState<EventLineupView | null>(null);
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState<SlotMap>({});
  const [selectedEntry, setSelectedEntry] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getMeetLineup(meetId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        const m: SlotMap = {};
        for (const s of res.slots)
          m[s.weightClass] = {
            rosterEntryId: s.rosterEntryId,
            wrestlerName: s.wrestlerName,
          };
        setSlots(m);
        setDirty(false);
      })
      .catch(() => !cancelled && setData(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [meetId]);

  // Roster wrestlers not yet assigned to any slot.
  const assignedIds = useMemo(
    () =>
      new Set(
        Object.values(slots)
          .filter(Boolean)
          .map((s) => s!.rosterEntryId),
      ),
    [slots],
  );
  const available = (data?.roster ?? []).filter(
    (r) => !assignedIds.has(r.rosterEntryId),
  );

  if (loading) return <div className="card">Loading lineup…</div>;
  if (!data || !data.weightClasses.length) {
    return (
      <div className="card">
        <h2>Lineup</h2>
        <p className="muted">
          {isAdmin
            ? "Set up a weight-class roster for the host team first, then build the lineup here."
            : "No lineup posted yet."}
        </p>
      </div>
    );
  }

  const assign = (weightClass: number, rosterEntryId: string) => {
    const wrestler = data.roster.find((r) => r.rosterEntryId === rosterEntryId);
    if (!wrestler) return;
    setSlots((prev) => {
      const next: SlotMap = { ...prev };
      // Remove this wrestler from any other slot (one slot per wrestler).
      for (const wc of Object.keys(next))
        if (next[Number(wc)]?.rosterEntryId === rosterEntryId)
          next[Number(wc)] = undefined;
      next[weightClass] = {
        rosterEntryId,
        wrestlerName: wrestler.wrestlerName,
      };
      return next;
    });
    setSelectedEntry(null);
    setDirty(true);
  };

  const clearSlot = (weightClass: number) => {
    setSlots((prev) => ({ ...prev, [weightClass]: undefined }));
    setDirty(true);
  };

  const onSlotActivate = (weightClass: number) => {
    if (!isAdmin) return;
    if (selectedEntry) assign(weightClass, selectedEntry);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = data.weightClasses
        .filter((w) => slots[w.base])
        .map((w) => ({
          weightClass: w.base,
          rosterEntryId: slots[w.base]!.rosterEntryId,
        }));
      await api.saveMeetLineup(meetId, payload);
      toast.success("Lineup saved");
      setDirty(false);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const filledCount = data.weightClasses.filter((w) => slots[w.base]).length;

  const printLineup = () => {
    const rows = data.weightClasses
      .map((w) => {
        const s = slots[w.base];
        return `<tr><td class="wt">${w.effective}</td><td>${
          s ? escapeHtml(s.wrestlerName) : "<span class='open'>—</span>"
        }</td></tr>`;
      })
      .join("");
    const title = escapeHtml(meetTitle ?? "Dual Meet Lineup");
    const when = meetDate
      ? new Date(meetDate).toLocaleDateString(undefined, {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : "";
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${title} — Lineup</title>
<style>
  body{font:15px/1.4 -apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#111;margin:2rem;}
  h1{margin:0 0 .15rem;font-size:1.5rem;} .sub{color:#555;margin:0 0 1rem;}
  table{border-collapse:collapse;width:100%;max-width:520px;}
  th,td{text-align:left;padding:.5rem .6rem;border-bottom:1px solid #ddd;}
  th{font-size:.8rem;text-transform:uppercase;letter-spacing:.04em;color:#666;}
  td.wt{font-weight:700;width:5rem;} .open{color:#aaa;}
  @media print{body{margin:0.5in;}}
</style></head><body>
  <h1>${title}</h1>
  <p class="sub">Dual meet lineup${when ? " · " + escapeHtml(when) : ""}</p>
  <table><thead><tr><th>Weight</th><th>Wrestler</th></tr></thead><tbody>${rows}</tbody></table>
  <script>window.onload=function(){window.print();}</script>
</body></html>`;
    const w = window.open("", "_blank", "width=640,height=800");
    if (w) {
      w.document.write(html);
      w.document.close();
    }
  };

  return (
    <div className="card">
      <div className="row spread">
        <h2 style={{ margin: 0 }}>Lineup</h2>
        <span className="row" style={{ gap: "0.4rem" }}>
          <span className="badge accent">
            {filledCount}/{data.weightClasses.length} set
          </span>
          {filledCount > 0 && (
            <button className="secondary" onClick={printLineup}>
              🖨 Print / share
            </button>
          )}
        </span>
      </div>
      {isAdmin && (
        <p className="muted" style={{ fontSize: "0.85rem", marginTop: "0.25rem" }}>
          Tap a wrestler then tap a weight to assign — or drag on desktop.
        </p>
      )}

      <div className="lineup-grid">
        {/* Weight-class slots */}
        <div className="lineup-slots">
          {data.weightClasses.map((w) => {
            const slot = slots[w.base];
            return (
              <div
                key={w.base}
                className={
                  "lineup-slot" +
                  (slot ? " filled" : "") +
                  (isAdmin && selectedEntry ? " droppable" : "")
                }
                onClick={() => onSlotActivate(w.base)}
                onDragOver={(e) => isAdmin && e.preventDefault()}
                onDrop={(e) => {
                  if (!isAdmin) return;
                  e.preventDefault();
                  const id = e.dataTransfer.getData("text/plain");
                  if (id) assign(w.base, id);
                }}
              >
                <span className="lineup-weight">{w.effective}</span>
                <span className="lineup-slot-name">
                  {slot ? (
                    <strong>{slot.wrestlerName}</strong>
                  ) : (
                    <span className="muted">open</span>
                  )}
                </span>
                {isAdmin && slot && (
                  <button
                    className="secondary lineup-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearSlot(w.base);
                    }}
                    aria-label={`Clear ${w.effective}`}
                  >
                    ✕
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Roster pool (admin only) */}
        {isAdmin && (
          <div className="lineup-pool">
            <h3 style={{ marginTop: 0 }}>Team roster</h3>
            {available.length === 0 && (
              <p className="muted">All rostered wrestlers are assigned.</p>
            )}
            {available.map((r) => (
              <button
                key={r.rosterEntryId}
                className={
                  "lineup-chip" +
                  (selectedEntry === r.rosterEntryId ? " selected" : "")
                }
                draggable
                onDragStart={(e) =>
                  e.dataTransfer.setData("text/plain", r.rosterEntryId)
                }
                onClick={() =>
                  setSelectedEntry((cur) =>
                    cur === r.rosterEntryId ? null : r.rosterEntryId,
                  )
                }
              >
                {r.wrestlerName}
                <span className="muted"> · {r.weightClass}</span>
                {r.teamId && r.teamId !== data.teamId && r.teamName && (
                  <span className="badge state" style={{ marginLeft: "0.4rem" }}>
                    {r.teamName}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="row" style={{ marginTop: "0.75rem" }}>
          <button disabled={!dirty || saving} onClick={save}>
            {saving ? "Saving…" : "Save lineup"}
          </button>
          {dirty && <span className="muted">Unsaved changes</span>}
        </div>
      )}
    </div>
  );
}
